import {
  Account,
  AdditionalHelpDetails,
  DeceasedRecord,
  DocumentsDetails,
  ExpressUiInfo,
  ServiceProvider,
} from "@/types/index";
import { ObjValueTypes } from "@/utils/Types";
import * as React from "react";
export enum ExpressStoreKey {
  // express form storage keys
  Case = "case",
  SingleAccount = "singleAccount",
  Deceased = "deceased",
  MultiAccount = "multiAccount",
  AdditionalServices = "additionalServices",
  UiInfo = "ui_info",
  DashboardInfo = "dashboard_info",
  ExpressSp = "express_sp",
}

export enum ExpressFormMenuKey {
  // express form menu keys
  // deceased and account comes underr account
  Account = "account",
  Documents = "documents",
  Kyc = "kyc",
  Notifier = "notifier",
  Executor = "executor",
  Nok = "nok",
  Submit = "submit",
}
interface ST<T> {
  source?: T;
  target?: T;
}
export interface ExpressFormStorage {
  slug?: string;
  sections?: ExpressSectionStorage;
}
export interface ExpressSectionStorage {
  [ExpressStoreKey.Case]?: { willAvailable?: boolean };
  [ExpressStoreKey.SingleAccount]?: ST<Account>;
  [ExpressStoreKey.Deceased]?: ST<DeceasedRecord>;
  [ExpressStoreKey.MultiAccount]?: ST<ReadonlyArray<Account>>;
  [ExpressStoreKey.AdditionalServices]?: any;
  [ExpressStoreKey.UiInfo]?: ExpressUiInfo;
  [ExpressStoreKey.DashboardInfo]?: any;
  [ExpressStoreKey.ExpressSp]?: ServiceProvider;
  [ExpressFormMenuKey.Account]?: any;
  [ExpressFormMenuKey.Documents]?: ST<DocumentsDetails>;
  [ExpressFormMenuKey.Kyc]?: any;
  [ExpressFormMenuKey.Notifier]?: any;
  [ExpressFormMenuKey.Executor]?: any;
  [ExpressFormMenuKey.Nok]?: any;
  [ExpressFormMenuKey.Submit]?: any;
}

export interface ExpressSubmissionData {
  slug?: string;
  [ExpressStoreKey.SingleAccount]?: Account;
  [ExpressStoreKey.Deceased]?: DeceasedRecord;
  [ExpressStoreKey.MultiAccount]?: ReadonlyArray<Account>;
  [ExpressStoreKey.AdditionalServices]?: any;
  [ExpressStoreKey.UiInfo]?: ExpressUiInfo;
  [ExpressStoreKey.DashboardInfo]?: any;
  [ExpressStoreKey.ExpressSp]?: ServiceProvider;
  [ExpressFormMenuKey.Account]?: any;
  [ExpressFormMenuKey.Documents]?: DocumentsDetails;
  [ExpressFormMenuKey.Kyc]?: any;
  [ExpressFormMenuKey.Notifier]?: any;
  [ExpressFormMenuKey.Executor]?: any;
  [ExpressFormMenuKey.Nok]?: any;
  [ExpressFormMenuKey.Submit]?: any;
  [ExpressStoreKey.Case]?: { willAvailable?: boolean };
}
export const getExpressData = () => {
  const str = sessionStorage.getItem("express-form-data");
  try {
    return str && JSON.parse(str);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const putExpressData = (data: any) => {
  sessionStorage.setItem("express-form-data", JSON.stringify(data));
};

export const getExpressSectionsData = () => {
  const data = getExpressData();
  return data?.sections;
};

export const getExpressSectionDataByKey = (key: ExpressStoreKey | ExpressFormMenuKey) => {
  const data = getExpressData();
  return data?.sections?.[key];
};

export const putExpressSectionDataByKey = (
  key: ExpressStoreKey | ExpressFormMenuKey,
  content: any
) => {
  const data = getExpressData() || {};
  data.sections = data.sections || {};
  data.sections[key] = content;
  putExpressData(data);
};

export const clearExpressSectionDataByKey = (key: ExpressStoreKey | ExpressFormMenuKey) => {
  const data = getExpressData() || {};
  data.sections = data.sections || {};
  data.sections[key] = undefined;
  putExpressData(data);
};

export const putExpressAdditionalServicesData = (content: AdditionalHelpDetails) => {
  putExpressSectionDataByKey(ExpressStoreKey.AdditionalServices, content);
};

export const getExpressSectionSourceDataByKey = (key: ExpressStoreKey | ExpressFormMenuKey) => {
  const data = getExpressSectionDataByKey(key);
  return data && data.source;
};

export const getExpressSectionTargetDataByKey = (key: ExpressStoreKey | ExpressFormMenuKey) => {
  const data = getExpressSectionDataByKey(key);
  return data && data.target;
};

export const setExpressSubmitted = (formUrl: string) => {
  const data = getExpressData() || {};
  data.submitted = true;
  data.formUrl = formUrl;
  putExpressData(data);
};

export const useExpressSectionDataByKey = (key: ExpressStoreKey | ExpressFormMenuKey) => {
  const [{ data, loaded }, setState] = React.useState({ data: undefined as any, loaded: false });

  const setData = React.useCallback(
    (data: any) => {
      setState({ data, loaded: true });
    },
    [setState]
  );

  React.useEffect(() => {
    const data = getExpressSectionSourceDataByKey(key);

    if (!data) {
      setData(undefined);
      return;
    }

    setData(data);
  }, [key, setData]);

  const saveData = React.useCallback(
    (data: any) => {
      putExpressSectionDataByKey(key, data);
    },
    [key]
  );

  return { data, loaded, setData, saveData };
};
export function getSectionData(
  sectionData: ExpressSectionStorage | undefined,
  key: ExpressFormMenuKey | ExpressStoreKey
): ObjValueTypes<ExpressSubmissionData> {
  if (!sectionData) return undefined;
  return sectionData?.[key]?.target;
}
